import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Skeleton,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Card,
  Chip,
  Avatar,
  Stack,
  Pagination,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Block as BlockIcon,
  Bolt as BoltIcon,
  Groups as GroupsIcon,
  Person2 as Person2Icon,
  Add as AddIcon,
  EmojiEvents as EmojiEventsIcon,
} from '@mui/icons-material';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { fetchBoards } from '../../store/boardSlice';
import { fetchWorkspaces } from '../../store/workspaceSlice';
import { showToast } from '../../store/uiSlice';
import { getWorkspaceRole, roleHasPermission, Permission } from '../../hooks/usePermissions';
import CreateBoardModal from '../../components/Workspace/CreateBoardModal';
import ActivityFeed from '../../components/Activity/ActivityFeed';
import dashboardApi from '../../api/endpoints/dashboardApi';
import { DashboardSummary, WorkspaceDashboardSummary } from '../../api/types/DashboardTypes';
import { tasksApi } from '../../api/endpoints/tasksApi';
import { Task, TaskStatus, TaskType } from '../../types/common';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import {
  SkeletonStagger,
  PageHeaderSkeleton,
  BoardTileSkeleton,
} from '../../components/common/Skeletons';
import './Dashboard.scss';

const STATUS_COLORS = {
  todo: '#94a3b8',
  inProgress: '#0073ea',
  review: '#ab47bc',
  testing: '#26c6da',
  done: '#00c875',
  blocked: '#ef5350',
};
const PRIORITY_COLORS = { low: '#00c875', medium: '#fdab3d', high: '#ef5350' };
const CONTRIBUTOR_COLORS = ['#0073ea', '#00c875', '#fdab3d', '#ab47bc', '#ef5350'];
const BOARD_ACCENTS = ['#0073ea', '#00c875', '#fdab3d', '#ab47bc', '#ef5350', '#26c6da', '#7986cb'];

const TYPE_LABELS: Record<string, string> = {
  [TaskType.FEATURE]: 'Feature',
  [TaskType.BUG]: 'Bug',
  [TaskType.QUALITY]: 'Quality',
  [TaskType.INFRASTRUCTURE]: 'Infra',
  [TaskType.RESEARCH]: 'Research',
  [TaskType.DOCUMENTATION]: 'Docs',
};
const TYPE_COLORS: Record<string, string> = {
  [TaskType.FEATURE]: '#0073ea',
  [TaskType.BUG]: '#ef5350',
  [TaskType.QUALITY]: '#00c875',
  [TaskType.INFRASTRUCTURE]: '#7986cb',
  [TaskType.RESEARCH]: '#26c6da',
  [TaskType.DOCUMENTATION]: '#fdab3d',
};

// Deterministic string hash so a board without a stored color always gets
// the same accent instead of a new random one on every render.
const hashString = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { boards, loading, error } = useAppSelector((state) => state.boards);
  const { workspaces, currentWorkspace } = useAppSelector((state) => state.workspaces);
  const { user } = useAppSelector((state) => state.auth);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('');

  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [workspaceSummary, setWorkspaceSummary] = useState<WorkspaceDashboardSummary | null>(null);
  const [workspaceSummaryLoading, setWorkspaceSummaryLoading] = useState(true);
  const PAGE_SIZE = 10;
  const [deadlinesPage, setDeadlinesPage] = useState(1);
  const [completedPage, setCompletedPage] = useState(1);
  const [myTasksPage, setMyTasksPage] = useState(1);

  const [myTasks, setMyTasks] = useState<Task[]>([]);
  const [myTasksLoading, setMyTasksLoading] = useState(true);
  const [myTasksError, setMyTasksError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchBoards());
    dispatch(fetchWorkspaces());
  }, [dispatch]);

  // Default the picker once workspaces load, but don't fight the user's
  // own choice on every re-render.
  useEffect(() => {
    if (!selectedWorkspaceId && (currentWorkspace || workspaces.length > 0)) {
      setSelectedWorkspaceId(currentWorkspace?.id || workspaces[0].id);
    }
  }, [selectedWorkspaceId, currentWorkspace, workspaces]);

  const targetWorkspaceId = selectedWorkspaceId || currentWorkspace?.id || workspaces[0]?.id;

  const targetWorkspace = useMemo(
    () => workspaces.find((w) => w.id === targetWorkspaceId) || currentWorkspace || null,
    [workspaces, targetWorkspaceId, currentWorkspace]
  );
  const canCreateBoard = roleHasPermission(
    getWorkspaceRole(targetWorkspace, user),
    Permission.BOARD_CREATE
  );

  useEffect(() => {
    let cancelled = false;
    setSummaryLoading(true);
    dashboardApi
      .getUserDashboardSummary()
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((err) => console.error('Failed to load dashboard summary', err))
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!targetWorkspaceId) {
      setWorkspaceSummaryLoading(false);
      return;
    }
    let cancelled = false;
    setWorkspaceSummaryLoading(true);
    dashboardApi
      .getWorkspaceDashboardSummary(targetWorkspaceId)
      .then((data) => {
        if (!cancelled) setWorkspaceSummary(data);
      })
      .catch((err) => console.error('Failed to load workspace dashboard summary', err))
      .finally(() => {
        if (!cancelled) setWorkspaceSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [targetWorkspaceId]);

  // A workspace switch invalidates whatever page the user was on.
  useEffect(() => {
    setDeadlinesPage(1);
    setCompletedPage(1);
  }, [targetWorkspaceId]);

  useEffect(() => {
    if (!user?.id) {
      setMyTasksLoading(false);
      return;
    }
    let cancelled = false;
    setMyTasksLoading(true);
    setMyTasksError(null);
    tasksApi
      .getUserTasks(user.id.toString())
      .then((res) => {
        if (!cancelled) setMyTasks(res.tasks);
      })
      .catch((err) => {
        console.error('Failed to load my tasks', err);
        if (!cancelled) setMyTasksError('Could not load your tasks. Try refreshing.');
      })
      .finally(() => {
        if (!cancelled) setMyTasksLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const handleNewBoardClick = () => {
    if (!targetWorkspaceId) {
      dispatch(
        showToast({
          message: 'Create a workspace first before adding a board.',
          type: 'error',
        })
      );
      return;
    }
    if (!canCreateBoard) {
      dispatch(
        showToast({
          message: "You don't have permission to create boards in this workspace.",
          type: 'error',
        })
      );
      return;
    }
    setCreateModalOpen(true);
  };

  const greetingName = (user?.name || '').split(' ')[0] || 'there';
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);
  const todayLabel = useMemo(
    () =>
      new Date().toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      }),
    []
  );

  const statusChartData = useMemo(() => {
    const s = workspaceSummary?.tasksByStatus || summary?.tasksByStatus;
    if (!s) return [];
    return [
      { key: 'todo', name: 'To Do', value: s.todo, color: STATUS_COLORS.todo },
      { key: 'inProgress', name: 'In Progress', value: s.inProgress, color: STATUS_COLORS.inProgress },
      { key: 'review', name: 'Review', value: s.review, color: STATUS_COLORS.review },
      { key: 'testing', name: 'Testing', value: s.testing, color: STATUS_COLORS.testing },
      { key: 'done', name: 'Done', value: s.done, color: STATUS_COLORS.done },
      { key: 'blocked', name: 'Blocked', value: s.blocked, color: STATUS_COLORS.blocked },
    ].filter((d) => d.value > 0);
  }, [workspaceSummary, summary]);

  const priorityChartData = useMemo(() => {
    const p = workspaceSummary?.tasksByPriority || summary?.tasksByPriority;
    if (!p) return [];
    return [
      { name: 'Low', value: p.low, color: PRIORITY_COLORS.low },
      { name: 'Medium', value: p.medium, color: PRIORITY_COLORS.medium },
      { name: 'High', value: p.high, color: PRIORITY_COLORS.high },
    ];
  }, [workspaceSummary, summary]);

  const contributorChartData = useMemo(
    () =>
      (workspaceSummary?.topContributors || []).map((c) => ({
        name: c.userName || c.userEmail || 'Unknown',
        completed: c.completedTasks,
      })),
    [workspaceSummary]
  );

  const upcomingDeadlines = workspaceSummary?.upcomingDeadlines || summary?.upcomingDeadlines || [];
  const recentlyCompleted = summary?.recentlyCompleted || [];

  const boardNameMap = useMemo(() => {
    const map = new Map<string, string>();
    (Array.isArray(boards) ? boards : []).forEach((b) => map.set(String(b.id), b.name));
    return map;
  }, [boards]);

  const PRIORITY_RANK: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 };
  const openMyTasks = useMemo(
    () =>
      myTasks
        .filter((t) => t.status !== TaskStatus.DONE)
        .sort((a, b) => {
          if (a.dueDate && b.dueDate) return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
          if (a.dueDate) return -1;
          if (b.dueDate) return 1;
          return (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9);
        }),
    [myTasks]
  );

  const myTasksByTypeData = useMemo(() => {
    const counts: Record<string, number> = {};
    openMyTasks.forEach((t) => {
      const key = t.type || 'other';
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).map(([type, value]) => ({
      name: TYPE_LABELS[type] || 'Other',
      value,
      color: TYPE_COLORS[type] || '#94a3b8',
    }));
  }, [openMyTasks]);

  const kpiCards = [
    {
      label: 'Total Tasks',
      value: summary?.totalTasks ?? 0,
      icon: <AssignmentIcon />,
      gradient: 'linear-gradient(135deg, #0073ea 0%, #4facfe 100%)',
    },
    {
      label: 'Completed',
      value: summary?.completedTasks ?? 0,
      sub:
        summary && summary.totalTasks > 0
          ? `${Math.round((summary.completedTasks / summary.totalTasks) * 100)}% done`
          : undefined,
      icon: <CheckCircleIcon />,
      gradient: 'linear-gradient(135deg, #00c875 0%, #6fe3a8 100%)',
    },
    {
      label: 'Overdue',
      value: summary?.overdueTasks ?? 0,
      icon: <ScheduleIcon />,
      gradient: 'linear-gradient(135deg, #ef5350 0%, #ff9a8b 100%)',
    },
    {
      label: 'Blocked',
      value: (workspaceSummary?.tasksByStatus || summary?.tasksByStatus)?.blocked ?? 0,
      icon: <BlockIcon />,
      gradient: 'linear-gradient(135deg, #d32f2f 0%, #ff8a80 100%)',
    },
    {
      label: 'Active Sprints',
      value: summary?.activeSprints ?? 0,
      icon: <BoltIcon />,
      gradient: 'linear-gradient(135deg, #fdab3d 0%, #ffd76f 100%)',
    },
    {
      label: 'My Tasks',
      value: summary?.myTasks ?? 0,
      icon: <Person2Icon />,
      gradient: 'linear-gradient(135deg, #ab47bc 0%, #d68fe0 100%)',
    },
    {
      label: 'Team Members',
      value: summary?.workspaceStats?.totalMembers ?? 0,
      icon: <GroupsIcon />,
      gradient: 'linear-gradient(135deg, #26c6da 0%, #7de3ef 100%)',
    },
  ];

  if (loading) {
    return (
      <div className="dashboard">
        <PageHeaderSkeleton />
        <div className="boards-grid">
          <SkeletonStagger>
            {Array.from({ length: 3 }).map((_, i) => (
              <BoardTileSkeleton key={i} />
            ))}
          </SkeletonStagger>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      </div>
    );
  }

  const boardsList = Array.isArray(boards) ? boards : [];

  return (
    <div className="dashboard">
      {/* Hero */}
      <Box className="dashboard-hero">
        <Box className="dashboard-hero__text">
          <Typography variant="overline" className="dashboard-hero__eyebrow">
            {todayLabel}
          </Typography>
          <Typography variant="h4" className="dashboard-hero__title">
            {greeting}, {greetingName} 👋
          </Typography>
          <Typography variant="body2" className="dashboard-hero__subtitle">
            Here's what's happening across your workspaces.
          </Typography>
        </Box>
        <Box className="dashboard-hero__actions">
          {workspaces.length > 1 && (
            <FormControl size="small" className="dashboard-hero__select">
              <Select
                value={targetWorkspaceId || ''}
                onChange={(e) => setSelectedWorkspaceId(e.target.value)}
                displayEmpty
              >
                {workspaces.map((ws) => (
                  <MenuItem key={ws.id} value={ws.id}>
                    {ws.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {canCreateBoard && (
            <button className="btn btn-hero" onClick={handleNewBoardClick}>
              <AddIcon sx={{ fontSize: 18 }} /> New Board
            </button>
          )}
        </Box>
      </Box>

      {/* KPI Cards */}
      <Box className="kpi-grid">
        {summaryLoading
          ? Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} className="kpi-card">
                <Skeleton variant="text" width="50%" height={18} />
                <Skeleton variant="text" width="35%" height={36} />
              </Card>
            ))
          : kpiCards.map((kpi) => (
              <Card key={kpi.label} className="kpi-card">
                <Box className="kpi-card__icon" sx={{ background: kpi.gradient }}>
                  {kpi.icon}
                </Box>
                <Box className="kpi-card__body">
                  <Typography variant="caption" className="kpi-card__label">
                    {kpi.label}
                  </Typography>
                  <Typography variant="h5" className="kpi-card__value">
                    {kpi.value}
                  </Typography>
                  {kpi.sub && (
                    <Typography variant="caption" className="kpi-card__sub">
                      {kpi.sub}
                    </Typography>
                  )}
                </Box>
              </Card>
            ))}
      </Box>

      {/* Charts row */}
      <Box className="charts-row">
        <Card className="chart-card">
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Tasks by Status
          </Typography>
          {workspaceSummaryLoading && summaryLoading ? (
            <Skeleton variant="rounded" width="100%" height={240} />
          ) : statusChartData.length === 0 ? (
            <Box className="chart-empty">No tasks yet</Box>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={statusChartData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={55}
                  outerRadius={85}
                  paddingAngle={3}
                >
                  {statusChartData.map((d) => (
                    <Cell key={d.key} fill={d.color} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
          <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="center" sx={{ mt: 1 }}>
            {statusChartData.map((d) => (
              <Box key={d.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: d.color }} />
                <Typography variant="caption" color="text.secondary">
                  {d.name} ({d.value})
                </Typography>
              </Box>
            ))}
          </Stack>
        </Card>

        <Card className="chart-card">
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Tasks by Priority
          </Typography>
          {workspaceSummaryLoading && summaryLoading ? (
            <Skeleton variant="rounded" width="100%" height={240} />
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={priorityChartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                <RechartsTooltip />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {priorityChartData.map((d) => (
                    <Cell key={d.name} fill={d.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="chart-card">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <EmojiEventsIcon sx={{ color: '#fdab3d', fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              Top Contributors
            </Typography>
          </Box>
          {workspaceSummaryLoading ? (
            <Skeleton variant="rounded" width="100%" height={240} />
          ) : contributorChartData.length === 0 ? (
            <Box className="chart-empty">No completed tasks yet</Box>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={contributorChartData} layout="vertical" margin={{ left: 12 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="name" width={90} tickLine={false} axisLine={false} />
                <RechartsTooltip />
                <Bar dataKey="completed" radius={[0, 6, 6, 0]}>
                  {contributorChartData.map((_, i) => (
                    <Cell key={i} fill={CONTRIBUTOR_COLORS[i % CONTRIBUTOR_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </Box>

      {/* My Focus */}
      <Box className="focus-row">
        <Card className="report-card focus-card">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Person2Icon sx={{ color: '#0073ea', fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              My Tasks
            </Typography>
            {openMyTasks.length > 0 && (
              <Chip size="small" label={openMyTasks.length} sx={{ bgcolor: 'rgba(0,115,234,0.1)', color: '#0073ea', fontWeight: 700 }} />
            )}
          </Box>
          {myTasksLoading ? (
            <Stack spacing={1}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={48} />
              ))}
            </Stack>
          ) : myTasksError ? (
            <Box className="chart-empty" sx={{ color: 'error.main' }}>{myTasksError}</Box>
          ) : openMyTasks.length === 0 ? (
            <Box className="chart-empty">You're all caught up 🎉</Box>
          ) : (
            <>
              <Stack spacing={1}>
                {openMyTasks
                  .slice((myTasksPage - 1) * PAGE_SIZE, myTasksPage * PAGE_SIZE)
                  .map((task) => (
                    <Box key={task.id} className="list-row" onClick={() => navigate(`/boards/${task.boardId}?taskId=${task.id}`)}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {task.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {boardNameMap.get(String(task.boardId)) || 'Board'}
                          {task.dueDate &&
                            ` · due ${formatDistanceToNow(new Date(task.dueDate), { addSuffix: true })}`}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={task.priority}
                        sx={{
                          textTransform: 'capitalize',
                          bgcolor: `${PRIORITY_COLORS[task.priority as keyof typeof PRIORITY_COLORS] || '#94a3b8'}22`,
                          color: PRIORITY_COLORS[task.priority as keyof typeof PRIORITY_COLORS] || '#64748b',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  ))}
              </Stack>
              {openMyTasks.length > PAGE_SIZE && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                  <Pagination
                    size="small"
                    count={Math.ceil(openMyTasks.length / PAGE_SIZE)}
                    page={myTasksPage}
                    onChange={(_, page) => setMyTasksPage(page)}
                  />
                </Box>
              )}
            </>
          )}
        </Card>

        <Card className="chart-card focus-card">
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            My Tasks by Type
          </Typography>
          {myTasksLoading ? (
            <Skeleton variant="rounded" width="100%" height={220} />
          ) : myTasksError ? (
            <Box className="chart-empty" sx={{ color: 'error.main' }}>{myTasksError}</Box>
          ) : myTasksByTypeData.length === 0 ? (
            <Box className="chart-empty">Nothing assigned to you yet</Box>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={myTasksByTypeData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
                    {myTasksByTypeData.map((d) => (
                      <Cell key={d.name} fill={d.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="center" sx={{ mt: 1 }}>
                {myTasksByTypeData.map((d) => (
                  <Box key={d.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: d.color }} />
                    <Typography variant="caption" color="text.secondary">
                      {d.name} ({d.value})
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </Card>
      </Box>

      {/* Deadlines / Completed / Activity */}
      <Box className="reports-row">
        <Card className="report-card">
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
            Upcoming Deadlines
          </Typography>
          {workspaceSummaryLoading && summaryLoading ? (
            <Stack spacing={1}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={48} />
              ))}
            </Stack>
          ) : upcomingDeadlines.length === 0 ? (
            <Box className="chart-empty">Nothing due soon 🎉</Box>
          ) : (
            <>
              <Stack spacing={1}>
                {upcomingDeadlines
                  .slice((deadlinesPage - 1) * PAGE_SIZE, deadlinesPage * PAGE_SIZE)
                  .map((task) => (
                    <Box key={task.id} className="list-row" onClick={() => navigate(`/boards/${task.board.id}`)}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {task.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {task.board?.name} · due {formatDistanceToNow(new Date(task.dueDate), { addSuffix: true })}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={task.priority}
                        sx={{
                          textTransform: 'capitalize',
                          bgcolor: `${PRIORITY_COLORS[task.priority as keyof typeof PRIORITY_COLORS] || '#94a3b8'}22`,
                          color: PRIORITY_COLORS[task.priority as keyof typeof PRIORITY_COLORS] || '#64748b',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  ))}
              </Stack>
              {upcomingDeadlines.length > PAGE_SIZE && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                  <Pagination
                    size="small"
                    count={Math.ceil(upcomingDeadlines.length / PAGE_SIZE)}
                    page={deadlinesPage}
                    onChange={(_, page) => setDeadlinesPage(page)}
                  />
                </Box>
              )}
            </>
          )}
        </Card>

        <Card className="report-card">
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
            Recently Completed
          </Typography>
          {summaryLoading ? (
            <Stack spacing={1}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={48} />
              ))}
            </Stack>
          ) : recentlyCompleted.length === 0 ? (
            <Box className="chart-empty">No tasks completed this week yet</Box>
          ) : (
            <>
              <Stack spacing={1}>
                {recentlyCompleted
                  .slice((completedPage - 1) * PAGE_SIZE, completedPage * PAGE_SIZE)
                  .map((task) => (
                    <Box key={task.id} className="list-row" onClick={() => navigate(`/boards/${task.board.id}`)}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: '#00c875' }}>
                        {(task.assignee?.name || '?').charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {task.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {task.board?.name} · {formatDistanceToNow(new Date(task.completedAt), { addSuffix: true })}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
              </Stack>
              {recentlyCompleted.length > PAGE_SIZE && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                  <Pagination
                    size="small"
                    count={Math.ceil(recentlyCompleted.length / PAGE_SIZE)}
                    page={completedPage}
                    onChange={(_, page) => setCompletedPage(page)}
                  />
                </Box>
              )}
            </>
          )}
        </Card>

        <Card className="report-card">
          {targetWorkspaceId ? (
            <ActivityFeed workspaceId={targetWorkspaceId} limit={PAGE_SIZE} />
          ) : (
            <Box className="chart-empty">Join a workspace to see activity</Box>
          )}
        </Card>
      </Box>

      {/* Boards */}
      <div className="dashboard-header dashboard-header--boards">
        <h2>Your Boards</h2>
      </div>
      <div className="boards-grid">
        {boardsList.length === 0 ? (
          <div className="empty-state">
            <p>No boards yet. Create your first board to get started!</p>
          </div>
        ) : (
          boardsList.map((board) => {
            const accent = board.color || BOARD_ACCENTS[hashString(board.name) % BOARD_ACCENTS.length];
            const initials = board.name
              .split(/\s+/)
              .filter(Boolean)
              .slice(0, 2)
              .map((w) => w[0]?.toUpperCase())
              .join('') || '?';
            return (
              <div
                key={board.id}
                className="board-tile"
                style={{ ['--accent' as any]: accent }}
                onClick={() => navigate(`/boards/${board.id}`)}
              >
                <div className="board-tile__glow" />
                <div className="board-tile__top">
                  <div className="board-tile__monogram">{initials}</div>
                  {board.key && <span className="board-tile__key">{board.key}</span>}
                </div>
                <h3 className="board-tile__name">{board.name}</h3>
                <p className="board-tile__desc">{board.description || 'No description yet'}</p>
                <div className="board-tile__footer">
                  <span className="board-tile__members">
                    <GroupsIcon sx={{ fontSize: 15 }} />
                    {(board.members || []).length || 1}
                  </span>
                  <span className="board-tile__updated">
                    Updated {formatDistanceToNow(new Date(board.updatedAt), { addSuffix: true })}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {targetWorkspaceId && (
        <CreateBoardModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          workspaceId={targetWorkspaceId}
        />
      )}
    </div>
  );
};

export default Dashboard;
